import { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertCircle, CheckCircle2, Database, X, Wifi, WifiOff } from 'lucide-react';
import { useMatchStatsFeedSSE, MatchStatsAttempt } from '@/hooks/useMatchStatsFeedSSE';
import { MatchStatsFeedDetailDialog } from './MatchStatsFeedDetailDialog';

interface Match {
  id: string;
  scheduleId?: string;
}

interface Props {
  match: Match | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function StatusBadge({ status }: { status: MatchStatsAttempt['status'] }) {
  if (status === 'success') {
    return (
      <Badge variant="default" className="bg-green-600 hover:bg-green-700 gap-1">
        <CheckCircle2 className="h-3 w-3" />
        success
      </Badge>
    );
  }
  if (status === 'cached') {
    return (
      <Badge variant="secondary" className="gap-1">
        <Database className="h-3 w-3" />
        cached
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" className="gap-1">
      <X className="h-3 w-3" />
      error
    </Badge>
  );
}

export function MatchStatsFeedSheet({ match, open, onOpenChange }: Props) {
  // Only open the SSE connection while the sheet is actually visible. The sheet
  // is mounted once per question on the live-scoring tab, so connecting on mount
  // (regardless of `open`) previously fired one stream per question. Gating on
  // `open` keeps at most one match-stats stream alive — the sheet the admin opened.
  const { attempts, consecutiveFailures, alertActive, connected } = useMatchStatsFeedSSE(
    open ? (match?.id ?? null) : null
  );
  const [selectedAttempt, setSelectedAttempt] = useState<MatchStatsAttempt | null>(null);

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-[900px] sm:max-w-[900px] overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle>Match Stats Feed</SheetTitle>
            {match && <p className="text-xs text-muted-foreground font-mono">{match.id}</p>}
            <div className="flex items-center gap-2 mt-1">
              {connected ? (
                <>
                  <span className="h-2 w-2 rounded-full bg-green-500 inline-block" />
                  <Wifi className="h-3 w-3 text-green-500" />
                  <span className="text-xs text-green-600 dark:text-green-400">Connected</span>
                </>
              ) : (
                <>
                  <span className="h-2 w-2 rounded-full bg-gray-400 inline-block" />
                  <WifiOff className="h-3 w-3 text-gray-400" />
                  <span className="text-xs text-muted-foreground">Disconnected</span>
                </>
              )}
            </div>
          </SheetHeader>

          {alertActive && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Feed failure alert: {consecutiveFailures} consecutive failures detected
              </AlertDescription>
            </Alert>
          )}

          {attempts.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
              No attempts recorded yet. Feed calls will appear here in real-time.
            </div>
          ) : (
            <Table>
              <TableHeader className="[&_tr]:border-b-0">
                <TableRow>
                  <TableHead className="bg-gray-100 dark:bg-gray-800 rounded-l-xl font-bold w-10">
                    #
                  </TableHead>
                  <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">
                    Timestamp
                  </TableHead>
                  <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Status</TableHead>
                  <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Duration</TableHead>
                  <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">
                    Team Stats
                  </TableHead>
                  <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">
                    Player Stats
                  </TableHead>
                  <TableHead className="bg-gray-100 dark:bg-gray-800 rounded-r-xl font-bold">
                    Error
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {attempts.map((attempt, index) => {
                  const isClickable = attempt.status === 'success';
                  const truncatedError =
                    attempt.error && attempt.error.length > 40
                      ? `${attempt.error.slice(0, 40)}...`
                      : attempt.error;

                  return (
                    <TableRow
                      key={attempt.id}
                      onClick={isClickable ? () => setSelectedAttempt(attempt) : undefined}
                      className={isClickable ? 'cursor-pointer hover:bg-muted/50' : undefined}
                    >
                      <TableCell className="text-muted-foreground text-xs">{index + 1}</TableCell>
                      <TableCell className="text-xs font-mono">
                        {new Date(attempt.timestamp).toLocaleTimeString()}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={attempt.status} />
                      </TableCell>
                      <TableCell className="text-xs">{attempt.durationMs}ms</TableCell>
                      <TableCell className="text-xs">
                        {attempt.teamStatsCount !== undefined ? attempt.teamStatsCount : '—'}
                      </TableCell>
                      <TableCell className="text-xs">
                        {attempt.playerStatsCount !== undefined ? attempt.playerStatsCount : '—'}
                      </TableCell>
                      <TableCell className="text-xs">
                        {attempt.error ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-destructive cursor-default">
                                  {truncatedError}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs break-words">
                                <p>{attempt.error}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </SheetContent>
      </Sheet>

      <MatchStatsFeedDetailDialog
        open={!!selectedAttempt}
        onOpenChange={o => {
          if (!o) setSelectedAttempt(null);
        }}
        attempt={selectedAttempt}
        fixtureId={match?.id ?? ''}
      />
    </>
  );
}
